'use client';

import { AlertTriangle, Lock, Phone, Search, UserX, Shield } from 'lucide-react';
import ContactUsCTA from '@/components/common/ContactCTA';
import { Feature } from '@/components/common/feature-variants';

export default function Page() {
  // Data arrays
  const reportSteps = [
    {
      icon: Phone,
      title: 'Contact Us Immediately',
      description: (
        <>
          <span className="block text-sm text-foreground/70">Email: support@paystop.io</span>
          <span className="block text-sm text-foreground/70">Phone: +237 676962723</span>
          <span className="block mt-4">
            <strong>Please provide:</strong>
          </span>
          <span className="block mt-2 space-y-1">
            <span className="block">• Description of fraudulent activity</span>
            <span className="block">• Date and time of suspicious transaction</span>
            <span className="block">• Involved parties</span>
            <span className="block">• Supporting documentation</span>
          </span>
        </>
      ),
    },
    {
      icon: Lock,
      title: 'Lock Your Account',
      description: (
        <span className="text-sm text-foreground/70">
          If you suspect unauthorised access, temporarily lock your account until the issue is
          investigated. Contact support or use the security features in your app.
        </span>
      ),
    },
    {
      icon: Shield,
      title: 'Change Account Details',
      description: (
        <span className="text-sm text-foreground/70">
          As an additional precaution, change your password, Payment PIN, and other security
          credentials to protect your account from unauthorized activity.
        </span>
      ),
    },
  ];

  const fraudTypes = [
    {
      icon: Search,
      title: 'Phishing Scams',
      description: (
        <span className="text-sm text-foreground/70">
          Fraudsters may attempt to trick you into revealing your personal details, account
          information, or PIN by pretending to be PayStop or another trusted entity.
        </span>
      ),
    },
    {
      icon: UserX,
      title: 'Unauthorised Account Access',
      description: (
        <span className="text-sm text-foreground/70">
          If someone gains access to your account without your permission, they may perform
          unauthorised transactions or withdrawals.
        </span>
      ),
    },
    {
      icon: UserX,
      title: 'Fake Agents or Sub-Agents',
      description: (
        <span className="text-sm text-foreground/70">
          Always ensure that you are dealing with verified PayStop agents or businesses. Fraudsters
          may pose as agents or offer fake services to trick you into sending money or revealing
          account details.
        </span>
      ),
    },
    {
      icon: UserX,
      title: 'Suspicious Transactions',
      description: (
        <span className="text-sm text-foreground/70">
          If you notice any transactions that you did not authorise or if an agent asks you to make
          an unusual payment, report it immediately.
        </span>
      ),
    },
  ];

  const investigationSteps = [
    {
      title: 'Initial Review',
      description:
        'We will review your report and gather all relevant information about the suspicious activity. This may include checking transaction logs and reviewing account details.',
    },
    {
      title: 'Investigation',
      description:
        'If fraud is suspected, our team will work to identify the perpetrators and prevent further harm. We may contact you for additional information if necessary.',
    },
    {
      title: 'Action Taken',
      description:
        'Depending on the findings, we will take appropriate action. This may include locking accounts, reversing fraudulent transactions, and reporting the incident to relevant authorities.',
    },
    {
      title: 'Preventative Measures',
      description:
        'We will provide recommendations to strengthen your account security and prevent future incidents. We may also update our internal systems to enhance fraud detection.',
    },
  ];

  const securityTips = [
    {
      title: 'Use Strong Passcode or Passwords',
      description:
        'Choose unique passcode or Passwords for your account and change them regularly.',
    },
    {
      title: 'Enable Multi-Factor Authentication',
      description: 'Always use Multi-Factor Authentication (MFA) for an added layer of security.',
    },
    {
      title: 'Keep Your Payment PIN Confidential',
      description:
        'Never share your 6-digit Payment PIN with anyone. This PIN is essential for securing your transactions.',
    },
    {
      title: 'Avoid Public Wi-Fi for Financial Transactions',
      description:
        'Never access your account or conduct financial transactions over public or unsecured Wi-Fi networks.',
    },
    {
      title: 'Verify Agent Details',
      description:
        'Ensure that you are dealing with verified and authorised PayStop agents. Always check the legitimacy of an agent before conducting any transactions.',
    },
  ];

  return (
    <>
      <section className="section-md bg-primary-100">
        <div className="container container-max">
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary">
              Report Fraud
            </h1>
            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              At PayStop, we take fraud prevention very seriously. Our platform is committed to
              ensuring the safety and security of your account and transactions. If you suspect any
              fraudulent activity involving your account or our services, please report it
              immediately. Your prompt action helps us protect your information and maintain the
              integrity of the PayStop network.
            </p>
          </div>
        </div>
      </section>

      <section className="section-sm">
        <div className="container space-y-12 container-md">
          <div className="p-4 rounded-xl bg-red-50">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Important Notice</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    If you suspect any fraudulent activity, please report it immediately. Your
                    prompt action helps protect your information and maintain the integrity of the
                    PayStop network.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-sm">
        <div className="container container-md">
          <div className="space-y-16">
            <div className="mx-auto lg:text-center">
              <h2 className="text-sm font-semibold leading-7 text-primary">Fraud Prevention</h2>
              <p className="mt-2 font-bold tracking-tight text-gray-900 title">
                How to Report Fraud
              </p>
            </div>
            <div>
              <div className="grid grid-cols-1 gap-x-10 gap-y-16 lg:grid-cols-3">
                {reportSteps.map((step, index) => (
                  <Feature
                    key={index}
                    description={step.description}
                    icon={<step.icon className="text-primary" />}
                    title={step.title}
                    variant="definition"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-md bg-primary-50 dark:bg-background">
        <div className="container container-md">
          <div className="space-y-16">
            <div className="mx-auto lg:text-center">
              <h2 className="text-sm font-semibold leading-7 text-primary">Investigation</h2>
              <p className="mt-2 font-bold tracking-tight text-gray-900 title">
                What Happens After Reporting Fraud?
              </p>
            </div>
            <div>
              <div className="grid grid-cols-1 gap-x-20 gap-y-16 lg:grid-cols-2">
                {investigationSteps.map((step, index) => (
                  <Feature
                    key={index}
                    description={step.description}
                    title={step.title}
                    showIcon={false}
                    variant="definition"
                    icon={''}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-md">
        <div className="container container-md">
          <div className="space-y-16">
            <div className="mx-auto space-y-5 lg:text-center">
              <p className="mt-2 font-bold tracking-tight text-gray-900 title">
                Common Types of Fraud to Watch Out For
              </p>
              <p>
                To help protect yourself from fraud, it’s important to be aware of common types of
                fraudulent activity:
              </p>
            </div>
            <div>
              <div className="grid grid-cols-1 gap-x-20 gap-y-16 lg:grid-cols-2">
                {fraudTypes.map((type, index) => (
                  <Feature
                    key={index}
                    description={type.description}
                    icon={<type.icon className="text-primary" />}
                    title={type.title}
                    variant="definition"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-md bg-slate-50 dark:bg-background">
        <div className="container container-md">
          <div className="space-y-16">
            <div className="mx-auto lg:text-center">
              <h2 className="text-sm font-semibold leading-7 text-primary">Security Tips</h2>
              <p className="mt-2 font-bold tracking-tight text-gray-900 title">
                How to Protect Yourself from Fraud
              </p>
            </div>
            <div>
              <dl className="grid grid-cols-1 gap-12 lg:grid-cols-3">
                {securityTips.map((tip, index) => (
                  <Feature
                    key={index}
                    description={tip.description}
                    title={tip.title}
                    showIcon={false}
                    variant="definition"
                    icon={''}
                  />
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-background">
        <div className="container space-y-24 container-max">
          <div className="relative px-8 py-16 overflow-hidden bg-gradient-to-b from-primary/20 to-background/5 rounded-3xl">
            <div className="inset-0 space-y-12">
              <div className="relative z-10 space-y-4 text-center">
                <h2 className="font-bold text-primary title">Your Security is Our Priority</h2>
                <p className="max-w-3xl mx-auto text-foreground/70">
                  At PayStop, we are committed to providing a secure and trustworthy platform for
                  all our users. By reporting any fraudulent activity, you help us keep the platform
                  safe for everyone. Thank you for your vigilance and cooperation in maintaining a
                  secure PayStop community.
                </p>
              </div>

              <ContactUsCTA
                className="bg-background border-primary-100"
                description={'Have questions or need assistance? We’re here to help!'}
                email={'support@paystop.io'}
                office={'23 Business Avenue, City, Country'}
                phone={'+237 676962723'}
                showOffice={false}
                title={'Need Assistance?'}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
